/**
 * LiveActivityFeed - Activity stream during task processing
 *
 * Shows a feed of what's happening on the backend:
 * - LLM calls and responses
 * - Tasks being created
 * - Events/commands being discovered
 * - Stage transitions
 *
 * Now uses polling-based progress messages that survive page refreshes.
 */
import React, { useEffect, useRef } from 'react';
import { Text, tokens } from '../design-system';

const LiveActivityFeed = ({ taskId, progressMessages = [], isProcessing = false }) => {
  const feedRef = useRef(null);

  // Auto-scroll to bottom when new activities arrive
  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [progressMessages]);

  // Build activities from progress messages
  const activities = progressMessages.length > 0
    ? progressMessages.map(msg => ({
        id: msg.id,
        type: msg.type || 'info',
        icon: msg.icon || '🔄',
        message: msg.message,
        timestamp: msg.timestamp,
      }))
    : [{
        id: 'start',
        type: 'info',
        icon: '🚀',
        message: 'Starting project analysis...',
        timestamp: new Date().toISOString(),
      }];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.liveIndicator}>
          <div style={{
            ...styles.liveDot,
            backgroundColor: isProcessing ? tokens.colors.success[500] : tokens.colors.neutral[400],
          }} />
          <Text style={{
            ...styles.liveText,
            color: isProcessing ? tokens.colors.success[500] : tokens.colors.neutral[400],
          }}>
            {isProcessing ? 'Live' : 'Done'}
          </Text>
        </div>
        <Text style={styles.title}>Activity</Text>
      </div>

      <div ref={feedRef} style={styles.feed}>
        {activities.map((activity, index) => (
          <ActivityItem key={activity.id || index} activity={activity} />
        ))}
        {isProcessing && <TypingIndicator />}
      </div>
    </div>
  );
};

const ActivityItem = ({ activity }) => {
  const timeAgo = getTimeAgo(activity.timestamp);

  return (
    <div style={{
      ...styles.activityItem,
      ...(activity.type === 'success' && styles.activitySuccess),
      ...(activity.type === 'milestone' && styles.activityMilestone),
      ...(activity.type === 'error' && styles.activityError),
    }}>
      <span style={styles.activityIcon}>{activity.icon}</span>
      <div style={styles.activityContent}>
        <Text style={styles.activityMessage}>{activity.message}</Text>
        {activity.detail && (
          <Text style={styles.activityDetail}>{activity.detail}</Text>
        )}
      </div>
      <Text style={styles.activityTime}>{timeAgo}</Text>
    </div>
  );
};

const TypingIndicator = () => (
  <div style={styles.typingIndicator}>
    <div style={styles.typingDots}>
      <div style={{ ...styles.typingDot, animationDelay: '0ms' }} />
      <div style={{ ...styles.typingDot, animationDelay: '150ms' }} />
      <div style={{ ...styles.typingDot, animationDelay: '300ms' }} />
    </div>
    <Text style={styles.typingText}>AI is thinking...</Text>
  </div>
);

function getTimeAgo(date) {
  if (!date) return '';
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  if (seconds < 5) return 'now';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h`;
}

const styles = {
  container: {
    backgroundColor: 'var(--color-bg-secondary)',
    borderRadius: tokens.borderRadius.lg,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: '300px',
    maxHeight: '60vh',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[3],
    padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
    borderBottom: '1px solid var(--color-border)',
  },

  liveIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[2],
  },

  liveDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: tokens.colors.success[500],
    animation: 'pulse 2s infinite',
  },

  liveText: {
    fontSize: tokens.typography.fontSize.xs[0],
    fontWeight: tokens.typography.fontWeight.semibold,
    color: tokens.colors.success[500],
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },

  title: {
    fontSize: tokens.typography.fontSize.sm[0],
    fontWeight: tokens.typography.fontWeight.medium,
    color: 'var(--color-text-muted)',
  },

  feed: {
    flex: 1,
    overflowY: 'auto',
    padding: tokens.spacing[3],
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[2],
  },

  activityItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacing[3],
    padding: tokens.spacing[2],
    borderRadius: tokens.borderRadius.md,
    backgroundColor: 'var(--color-bg)',
    animation: 'slideIn 0.2s ease-out',
  },

  activitySuccess: {
    backgroundColor: `${tokens.colors.success[500]}15`,
    borderLeft: `3px solid ${tokens.colors.success[500]}`,
  },

  activityMilestone: {
    backgroundColor: `${tokens.colors.primary[500]}15`,
    borderLeft: `3px solid ${tokens.colors.primary[500]}`,
  },

  activityError: {
    backgroundColor: `${tokens.colors.error[500]}15`,
    borderLeft: `3px solid ${tokens.colors.error[500]}`,
  },

  activityIcon: {
    fontSize: '16px',
    flexShrink: 0,
    marginTop: '2px',
  },

  activityContent: {
    flex: 1,
    minWidth: 0,
  },

  activityMessage: {
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text)',
    lineHeight: 1.4,
  },

  activityDetail: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: 'var(--color-text-muted)',
    marginTop: tokens.spacing[1],
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  activityTime: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: 'var(--color-text-muted)',
    flexShrink: 0,
  },

  typingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[3],
    padding: tokens.spacing[2],
    opacity: 0.7,
  },

  typingDots: {
    display: 'flex',
    gap: '4px',
  },

  typingDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-text-muted)',
    animation: 'bounce 1s infinite',
  },

  typingText: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: 'var(--color-text-muted)',
    fontStyle: 'italic',
  },
};

// Add keyframe animations
if (typeof document !== 'undefined' && !document.getElementById('live-activity-feed-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'live-activity-feed-styles';
  styleSheet.textContent = `
    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes bounce {
      0%, 60%, 100% {
        transform: translateY(0);
      }
      30% {
        transform: translateY(-4px);
      }
    }

    @keyframes pulse {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }
  `;
  document.head.appendChild(styleSheet);
}

export default LiveActivityFeed;
